import { useEffect } from "react";

// Hooks
import useMyPace from "./useMyPace";
import { useIntl } from "react-intl";

// Mui
import { Box, Skeleton, Theme, Typography } from "@mui/material";

// Components
import CustomIcon from "components/CustomIcon";
import { BehaviouralBreakdown } from "components/Charts";
import NavButton from "components/HeaderNavigation/NavButton";
import { Body, Header } from "components/Layout/LayoutContent";
import { MyAssessmentCard } from "components/Pace/MyAssessmentCard";

// Icons
import icons from "enums/icons";

// Types
import { RootState } from "app/store";

const MyPace = () => {
    const intl = useIntl();

    const {
        status,
        chartData,
        reportRequest,
        shouldShowBanner,
        retakeAvailableOn,
        previousReportsList,
        hasCompletedAssessment,
        hasOnGoingAssessment,
        handleRetestPace,
        handlenavigateToMyDashboard,
        handleUpdatePreviousList,
    } = useMyPace();

    useEffect(() => {
        if (reportRequest === "done" && !hasCompletedAssessment) {
            handlenavigateToMyDashboard();
        }
    }, [handlenavigateToMyDashboard, hasCompletedAssessment, reportRequest]);

    if (reportRequest !== "done") return null;

    return (
        <>
            <Header backgroundColor="primary" sx={{ paddingTop: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Typography variant="h5" fontWeight={600}>
                        My PACE
                    </Typography>
                    <NavButton
                        icon={null}
                        variant="outlined"
                        showBackArrow={false}
                        onClick={handlenavigateToMyDashboard}
                    >
                        Back To My Dashboard
                    </NavButton>
                </Box>
            </Header>

            {/* Banner */}
            {shouldShowBanner && (
                <Box sx={(theme: Theme) => ({ display: "flex", backgroundColor: theme.palette.info.main, padding: 2 })}>
                    <CustomIcon icon={icons.paceIconPrevious} sx={{ fontSize: 28, mt: 0.5 }} />
                    <Box ml={1}>
                        <Typography variant="subtitle1" fontWeight={700} color="common.white">
                            {intl.formatMessage({ id: "pace.myPace.banner.text01" })}
                        </Typography>
                        <Typography variant="subtitle1" color="common.white">
                            {intl.formatMessage({ id: "pace.myPace.banner.text02" })}
                        </Typography>
                    </Box>
                </Box>
            )}

            <Body sx={{ padding: 2 }}>
                <Typography variant="subtitle1" color="secondary.main" mb={4}>
                    {intl.formatMessage({ id: "pace.myPace.description" })}
                </Typography>
                <Box display="flex">
                    <Box sx={(theme) => ({ flex: 1, overflow: "hidden", paddingRight: theme.spacing(2.3) })}>
                        {chartData.status === "done" ? (
                            <BehaviouralBreakdown
                                width={0}
                                height={0}
                                getData={(store: RootState) => chartData.data}
                                moreOptions={{ shouldShowPaceDefinitions: true, hideCustomizeLabels: false }}
                            />
                        ) : (
                            <Skeleton animation="wave" variant="rounded" sx={{ height: "70vh" }} />
                        )}
                    </Box>
                    <Box width="30%">
                        {reportRequest === "done" ? (
                            <MyAssessmentCard
                                statusLegend={status.legend}
                                statusBarColor={status.barColor}
                                isRetakeAvailable={status.isRetakeAvailable}
                                handleRetestPace={handleRetestPace}
                                retakeAvailableOn={retakeAvailableOn.current}
                                previousReportsList={previousReportsList}
                                handleUpdatePreviousList={handleUpdatePreviousList}
                                hasOngoingAssessment={hasOnGoingAssessment}
                            />
                        ) : (
                            <Skeleton animation="wave" variant="rounded" sx={{ height: "70vh" }} />
                        )}
                    </Box>
                </Box>
            </Body>
        </>
    );
};

export default MyPace;
